import React, { useState, useEffect } from "react";

import {
  Backdrop,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
} from "@material-ui/core";

import { AreaType } from "../../../../utils/constants";
import {
  isTrack,
  isUnmanagedAisle,
  isValidTrackLength,
} from "../../../../utils/locationUtils";
import { handleNumberFieldKeyDown } from "../../../../utils/utils";

/**
 * Dialog for editing an unstructured (track) aisle: name, zone, and track length.
 *
 * @param {Object} props
 * @param {{ id?: string, name?: string, zoneId?: string, length?: number | null, area?: { id: string, name?: string, areaType?: string } }} props.selectedLocation - The aisle/location being edited.
 * @param {{ zones: { items: Array<{ id: string, name: string }> } }} props.area - Area containing zones for the zone dropdown.
 * @param {(zoneId: string) => void} props.setAisleZone
 * @param {(name: string) => void} props.setNewLocationName
 * @param {(evt: React.ChangeEvent<HTMLInputElement>) => void} props.setTrackLength
 * @param {Record<string, string>} props.classes - MUI class names.
 * @param {() => Promise<void>} props.updateAisleAndLocation
 * @param {boolean} props.open - Whether the dialog is open.
 * @param {(evt?: React.SyntheticEvent) => void} props.handleClose
 * @param {boolean} props.loading - Whether an update is in progress.
 * @returns {JSX.Element}
 */
export const UnstructuredAisleDialog = ({
  selectedLocation,
  area,
  setAisleZone,
  setNewLocationName,
  setTrackLength,
  classes,
  updateAisleAndLocation,
  open,
  handleClose,
  loading,
}) => {
  const [initialName, setInitialName] = useState(selectedLocation.name);
  const [initialZoneId, setInitialZoneId] = useState(selectedLocation.zoneId);
  const [initialLength, setInitialLength] = useState(
    selectedLocation.length == null ? null : selectedLocation.length
  );
  const [trackLengthValue, setTrackLengthValue] = useState(
    isTrack(selectedLocation.area) && selectedLocation.length != null
      ? selectedLocation.length
      : ""
  );

  useEffect(() => {
    // Reset initial snapshot values when location ID or area changes
    setInitialName(selectedLocation.name);
    setInitialZoneId(selectedLocation.zoneId);
    setInitialLength(selectedLocation.length == null ? null : selectedLocation.length);
  }, [selectedLocation.area, selectedLocation.id]);

  useEffect(() => {
    // Sync working track length value with prop changes
    if (isTrack(selectedLocation.area)) {
      const newLength =
        selectedLocation.length != null ? selectedLocation.length : "";
      setTrackLengthValue(newLength);
    } else {
      setTrackLengthValue("");
    }
  }, [selectedLocation.area, selectedLocation.length]);

  const doUpdateAisle = async (evt) => {
    const trackLengthChanged =
      isTrack(selectedLocation.area) &&
      (selectedLocation.length == null ? null : selectedLocation.length) !==
        initialLength;
    if (
      selectedLocation.name === initialName &&
      selectedLocation.zoneId === initialZoneId &&
      !trackLengthChanged
    ) {
      // cancel edit - no changes
      handleClose(evt);
      return;
    }
    // await updateAisleAndLocation(bulkCapacity, bulkUnits);
    await updateAisleAndLocation();
  };

  const isSubmitButtonDisabled = () => {
    // TODO: Remove - no longer needed
    // if (area.areaType === AreaType.BULK) {
    //     invalid = invalid || !bulkCapacity || !bulkUnits;
    // }
    return (
      !selectedLocation.name ||
      (isTrack(selectedLocation.area) && !isValidTrackLength(trackLengthValue))
    );
  };

  const getTitle = () => {
    // TODO: Remove - no longer needed
    // if (selectedLocation.area.areaType === AreaType.BULK) return "Edit Location";
    if (isTrack(selectedLocation.area)) return "Edit Track";
    return "Edit Aisle";
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <DialogTitle id="rename-location">{getTitle()}</DialogTitle>
      <form>
        <DialogContent>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField
                id="area"
                label="Area"
                size="small"
                variant="outlined"
                fullWidth
                disabled
                className={classes.locationEditInput}
                value={selectedLocation.area?.name || ""}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="zone"
                label="Zone"
                size="small"
                variant="outlined"
                fullWidth
                select
                required
                value={selectedLocation.zoneId}
                onChange={(evt) => setAisleZone(evt.target.value)}
                className={classes.locationEditInput}
              >
                {area.zones.items.map((itm) => (
                  <MenuItem key={itm.id} value={itm.id}>
                    {itm.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="name"
                label="Name"
                size="small"
                variant="outlined"
                fullWidth
                autoFocus
                required
                value={selectedLocation.name}
                onChange={setNewLocationName}
                className={classes.locationEditInput}
              />
            </Grid>
            {isTrack(selectedLocation.area) && (
              <Grid item xs={12}>
                <TextField
                  id="track-length"
                  label="Track Length"
                  size="small"
                  variant="outlined"
                  fullWidth
                  required
                  type="number"
                  inputProps={{ min: 1, step: 0.1 }}
                  value={trackLengthValue}
                  onChange={(evt) => {
                    setTrackLengthValue(evt.target.value);
                    setTrackLength(evt);
                  }}
                  onKeyDown={handleNumberFieldKeyDown}
                  className={classes.locationEditInput}
                  helperText="Required; minimum 1 ft"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">ft</InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}
            {isUnmanagedAisle(selectedLocation) &&
              !isTrack(selectedLocation.area) && (
                <Grid item xs={12}>
                  <TextField
                    id="capacity-used"
                    label="Capacity Used"
                    size="small"
                    variant="outlined"
                    fullWidth
                    disabled
                    value={selectedLocation.capacityUsed}
                    className={classes.locationEditInput}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={doUpdateAisle}
            color="primary"
            disabled={isSubmitButtonDisabled()}
          >
            Submit
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
